import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Plus } from "lucide-react";
import api from "@/lib/api";
import { chf } from "./salairesUtils";

const MAPPING_LABELS = {
  charge_salaires: "Salaires bruts (charge)",
  charge_avs: "Charges sociales AVS/AI/APG/AC/AF (charge)",
  charge_lpp: "Prévoyance professionnelle (charge)",
  charge_laa: "Assurance-accidents (charge)",
  charge_ijm: "Indemnités journalières maladie (charge)",
  charge_cct: "Contributions CCT (charge)",
  charge_autres: "Autres charges de personnel",
  dette_salaires: "Salaires nets à payer (dette)",
  dette_avs: "Assurances sociales à payer (dette)",
  dette_lpp: "LPP à payer (dette)",
  dette_laa: "LAA à payer (dette)",
  dette_ijm: "IJM à payer (dette)",
  dette_cct: "CCT à payer (dette)",
  dette_is: "Impôts à la source à payer (dette)",
  dette_autres_retenues: "Retenues diverses à payer (dette)",
  provision_13e: "Provision 13e salaire",
  avance_personnel: "Avances au personnel",
  banque: "Compte bancaire de paiement",
};

export default function SalairesParametresTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(null);
  const [mapping, setMapping] = useState({});
  const [comptes, setComptes] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: plan }] = await Promise.all([
        api.get("/salaires/parametres"),
        api.get("/journal/plan-comptes"),
      ]);
      setData(d);
      setVersion(JSON.parse(JSON.stringify(d.actuelle)));
      setMapping(d.mapping || {});
      setComptes(plan || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const setRub = (code, champ, valeur) => setVersion((v) => ({
    ...v,
    rubriques: { ...v.rubriques, [code]: { ...v.rubriques[code], [champ]: valeur } },
  }));
  const setLpp = (champ, valeur) => setVersion((v) => ({ ...v, lpp: { ...v.lpp, [champ]: valeur } }));
  const setPlanTranche = (idx, champ, valeur) => setVersion((v) => {
    const plans = JSON.parse(JSON.stringify(v.lpp.plans));
    plans[0].tranches_age[idx][champ] = Number(valeur);
    return { ...v, lpp: { ...v.lpp, plans } };
  });
  const setPlan = (champ, valeur) => setVersion((v) => {
    const plans = JSON.parse(JSON.stringify(v.lpp.plans));
    plans[0][champ] = valeur;
    return { ...v, lpp: { ...v.lpp, plans } };
  });

  const enregistrer = async () => {
    setBusy(true);
    try {
      await api.post("/salaires/parametres", {
        date_debut: version.date_debut,
        commentaire: version.commentaire,
        rubriques: version.rubriques,
        lpp: version.lpp,
        arrondi_cotisations: version.arrondi_cotisations,
      });
      toast.success(`Paramètres enregistrés avec effet au ${version.date_debut}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const enregistrerMapping = async () => {
    setBusy(true);
    try {
      await api.put("/salaires/mapping-comptes", mapping);
      toast.success("Correspondance comptable enregistrée");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const nouvelleVersion = () => {
    const d = new Date();
    const an = d.getFullYear() + 1;
    setVersion((v) => ({ ...v, date_debut: `${an}-01-01`, commentaire: `Taux ${an}` }));
    toast.info(`Modifiez les taux puis enregistrez : ils s'appliqueront dès le 01.01.${an}`);
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>;
  if (!version) return null;

  return (
    <div className="space-y-5" data-testid="salaires-parametres">
      <Card className="p-4 border-gray-200">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs">Date d'entrée en vigueur</Label>
            <Input type="date" value={version.date_debut || ""} onChange={(e) => setVersion((v) => ({ ...v, date_debut: e.target.value }))}
                   className="mt-1 w-44" data-testid="params-date-debut" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Commentaire</Label>
            <Input value={version.commentaire || ""} onChange={(e) => setVersion((v) => ({ ...v, commentaire: e.target.value }))}
                   className="mt-1" data-testid="params-commentaire" />
          </div>
          <Button variant="outline" onClick={nouvelleVersion} data-testid="params-nouvelle-version">
            <Plus className="w-4 h-4 mr-2" /> Nouveaux taux à une date
          </Button>
          <Button onClick={enregistrer} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="params-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Les fiches déjà validées gardent les taux de leur période : modifier ces valeurs ne recalcule jamais le passé.
          {data?.versions?.length > 1 && ` ${data.versions.length} versions enregistrées.`}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
              <tr>
                <th className="px-2 py-2">Rubrique</th>
                <th className="px-2 py-2 text-right">Taux employé %</th>
                <th className="px-2 py-2 text-right">Taux employeur %</th>
                <th className="px-2 py-2 text-right">Plafond annuel</th>
                <th className="px-2 py-2 text-right">Franchise annuelle</th>
                <th className="px-2 py-2 text-center">Active</th>
              </tr>
            </thead>
            <tbody data-testid="params-rubriques">
              {Object.entries(version.rubriques || {}).map(([code, r]) => (
                <tr key={code} className="border-t border-gray-100">
                  <td className="px-2 py-1.5">
                    <div className="font-medium text-gray-800">{r.libelle}</div>
                    <div className="text-[10px] text-gray-400 uppercase">{code}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.001" value={r.taux_employe ?? 0}
                           onChange={(e) => setRub(code, "taux_employe", Number(e.target.value))}
                           className="h-8 text-right" data-testid={`rub-${code}-emp`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="0.001" value={r.taux_employeur ?? 0}
                           onChange={(e) => setRub(code, "taux_employeur", Number(e.target.value))}
                           className="h-8 text-right" data-testid={`rub-${code}-empr`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="100" value={r.plafond_annuel ?? ""}
                           onChange={(e) => setRub(code, "plafond_annuel", e.target.value === "" ? null : Number(e.target.value))}
                           className="h-8 text-right" placeholder="—" data-testid={`rub-${code}-plafond`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="number" step="100" value={r.franchise_annuelle ?? ""}
                           onChange={(e) => setRub(code, "franchise_annuelle", e.target.value === "" ? null : Number(e.target.value))}
                           className="h-8 text-right" placeholder="—" data-testid={`rub-${code}-franchise`} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={r.actif !== false} onChange={(e) => setRub(code, "actif", e.target.checked)}
                           className="rounded border-gray-300 text-[#52a675]" data-testid={`rub-${code}-actif`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 border-gray-200">
        <h3 className="font-heading font-semibold text-gray-900 mb-3">Prévoyance professionnelle (LPP)</h3>
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            ["seuil_entree", "Seuil d'entrée"],
            ["deduction_coordination", "Déduction de coordination"],
            ["salaire_assure_min", "Salaire assuré minimum"],
            ["salaire_assure_max", "Salaire assuré maximum"],
            ["salaire_lpp_max", "Salaire LPP maximum"],
          ].map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs text-gray-600">{l}</Label>
              <Input type="number" step="10" value={version.lpp?.[k] ?? 0}
                     onChange={(e) => setLpp(k, Number(e.target.value))}
                     className="mt-1" data-testid={`lpp-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-gray-600">Part employé (%)</Label>
            <Input type="number" step="1" value={version.lpp?.plans?.[0]?.repartition_employe_pct ?? 50}
                   onChange={(e) => setPlan("repartition_employe_pct", Number(e.target.value))}
                   className="mt-1" data-testid="lpp-part-employe" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Taux de risque / frais (%)</Label>
            <Input type="number" step="0.01" value={version.lpp?.plans?.[0]?.taux_risque ?? 0}
                   onChange={(e) => setPlan("taux_risque", Number(e.target.value))}
                   className="mt-1" data-testid="lpp-taux-risque" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Frais fixes (CHF/mois)</Label>
            <Input type="number" step="0.05" value={version.lpp?.plans?.[0]?.frais_mensuel ?? 0}
                   onChange={(e) => setPlan("frais_mensuel", Number(e.target.value))}
                   className="mt-1" data-testid="lpp-frais" />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-gray-600">Bonifications de vieillesse par tranche d'âge (%)</Label>
          <div className="grid sm:grid-cols-4 gap-3 mt-1">
            {(version.lpp?.plans?.[0]?.tranches_age || []).map((t, i) => (
              <div key={i} className="flex items-center gap-1">
                <Input type="number" value={t.age_min} onChange={(e) => setPlanTranche(i, "age_min", e.target.value)}
                       className="w-16" data-testid={`lpp-tranche-${i}-min`} />
                <span className="text-xs text-gray-400">→</span>
                <Input type="number" value={t.age_max} onChange={(e) => setPlanTranche(i, "age_max", e.target.value)}
                       className="w-16" data-testid={`lpp-tranche-${i}-max`} />
                <Input type="number" step="0.1" value={t.taux} onChange={(e) => setPlanTranche(i, "taux", e.target.value)}
                       className="w-20" data-testid={`lpp-tranche-${i}-taux`} />
                <span className="text-xs text-gray-400">%</span>
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
            <input type="checkbox" checked={version.lpp?.plans?.[0]?.inclure_13e !== false}
                   onChange={(e) => setPlan("inclure_13e", e.target.checked)}
                   className="rounded border-gray-300 text-[#52a675]" data-testid="lpp-inclure-13e" />
            Inclure le 13e salaire dans le salaire annuel de référence
          </label>
        </div>
      </Card>

      <Card className="p-4 border-gray-200">
        <h3 className="font-heading font-semibold text-gray-900 mb-1">Correspondance comptable</h3>
        <p className="text-xs text-gray-500 mb-3">
          Chaque rubrique est affectée à un compte de votre plan comptable.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(MAPPING_LABELS).map(([cle, label]) => (
            <div key={cle}>
              <Label className="text-xs text-gray-600">{label}</Label>
              <select
                value={mapping[cle] || ""}
                onChange={(e) => setMapping((m) => ({ ...m, [cle]: e.target.value }))}
                className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                data-testid={`mapping-${cle}`}
              >
                <option value="">—</option>
                {comptes.map((c) => (
                  <option key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <Button onClick={enregistrerMapping} disabled={busy} className="mt-4 bg-[#52a675] hover:bg-[#3d8a5c]"
                data-testid="mapping-save">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer la correspondance
        </Button>
      </Card>

      <Card className="p-4 border-gray-200">
        <h3 className="font-heading font-semibold text-gray-900 mb-2">Arrondi</h3>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-gray-600">Pas d'arrondi des cotisations (CHF)</Label>
            <Input type="number" step="0.01" value={version.arrondi_cotisations ?? 0.05}
                   onChange={(e) => setVersion((v) => ({ ...v, arrondi_cotisations: Number(e.target.value) }))}
                   className="mt-1 w-32" data-testid="params-arrondi" />
          </div>
          <span className="text-xs text-gray-500 pb-2">
            0.05 = arrondi aux 5 centimes (usage suisse) · ex. {chf(315.7945)} → {chf(315.8)}
          </span>
        </div>
      </Card>
    </div>
  );
}
